import { IconButton, Icons, QuantityButton, Stack } from "@caspeco/casper-ui-library";
import { ErrorType } from "components/selection-components/preorder/widgets/error-type";
import { clickTrackingService } from "logger/click-tracking";
import { useEffect, useRef, useState } from "react";

/**
 * Gives an icon button if the quantity is 1. Gives quantity buttons if the quantity is greater than 1.
 * Have to ability to show inline errors if there is a minimum or maximum quantity that is exceeded.
 */
const AmountSelector = ({
    currentQuantity,
    totalGroupQuantities,
    setQuantity,
    addItemButtonText,
    minimumQuantity = Number.MIN_SAFE_INTEGER,
    maximumQuantity = Number.MAX_SAFE_INTEGER,
    minWarning = "Minimum quantity reached",
    maxWarning = "Maximum quantity reached",
    onErrorChange,
    disableAdd = false,
    blockAdd = false,
    customConstraintCheck,
    hasLoadedStocks,
    disabled = false,
}: {
    currentQuantity: number;
    totalGroupQuantities: number;
    addItemButtonText: string;
    setQuantity: (quantity: number, resetMessage: boolean) => void;
    minimumQuantity?: number;
    maximumQuantity?: number;
    minWarning?: string;
    maxWarning?: string;
    onErrorChange: (isError: boolean, message: string) => void;
    disableAdd?: boolean;
    blockAdd?: boolean;
    customConstraintCheck?: (action: string) => {
        isValid: boolean;
        message: string;
        errorType: ErrorType;
    };
    hasLoadedStocks?: boolean;
    disabled?: boolean;
}) => {
    const [error, setError] = useState(ErrorType.None);
    const [disableAddState, setDisableAddState] = useState(disableAdd);
    const mountedRef = useRef(false);

    const checkConstraints = (action: "add" | "remove"): boolean => {
        if (customConstraintCheck) {
            const { isValid, message, errorType } = customConstraintCheck(action);
            if (!isValid) {
                setError(errorType);
                onErrorChange(true, message);
                return false;
            }
        }

        if (action === "add" && totalGroupQuantities >= maximumQuantity) {
            setError(ErrorType.Maximum);
            onErrorChange(true, maxWarning);
            return false;
        }

        if (action === "remove" && totalGroupQuantities <= minimumQuantity && currentQuantity > 0) {
            setError(ErrorType.Minimum);
            onErrorChange(true, minWarning);
            return false;
        }

        if (error !== ErrorType.None) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }
        return true;
    };

    useEffect(() => {
        if (mountedRef.current || !hasLoadedStocks) return;
        mountedRef.current = true;

        if (!customConstraintCheck) return;
        const { isValid, message, errorType } = customConstraintCheck("add");

        if (!isValid) {
            setDisableAddState(true);
            setError(errorType);
            onErrorChange(isValid, message);
        }
    }, [customConstraintCheck, hasLoadedStocks, onErrorChange]);

    useEffect(() => {
        setDisableAddState(disableAdd);
    }, [disableAdd]);

    // Check if we're no longer in an error state when totalGroupQuantities changes
    useEffect(() => {
        // Only run if we're in an error state
        if (error === ErrorType.None) return;

        if (error === ErrorType.Maximum && totalGroupQuantities < maximumQuantity) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }

        if (error === ErrorType.Minimum && totalGroupQuantities > minimumQuantity) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }
    }, [totalGroupQuantities, error, minimumQuantity, maximumQuantity, onErrorChange]);

    if (currentQuantity === 0) {
        return (
            <IconButton
                icon={Icons.Add}
                variant="tertiary"
                aria-label={addItemButtonText}
                isDisabled={disabled || disableAddState}
                onClick={() => {
                    const passedConstraints = checkConstraints("add");
                    setQuantity(currentQuantity + 1, passedConstraints);
                }}
                data-track-click="preorder-item-add-iconbutton"
            />
        );
    }

    return (
        <Stack>
            <QuantityButton
                quantity={currentQuantity}
                max={999}
                disabled={disabled}
                onClickMinus={() => {
                    clickTrackingService.recordClick("preorder-item-minus-quantitybutton");
                    const passedConstraints = checkConstraints("remove");
                    setQuantity(currentQuantity - 1, passedConstraints);
                }}
                onClickPlus={() => {
                    clickTrackingService.recordClick("preorder-item-plus-quantitybutton");
                    const passedConstraints = checkConstraints("add");
                    if (!blockAdd) setQuantity(currentQuantity + 1, passedConstraints);
                }}
            />
        </Stack>
    );
};

export default AmountSelector;
